import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "./lib/AuthContext";
import { sendPin } from "./lib/auth";

/**
 * Sign-in gate — SMS one-time code only.
 *
 * Replaces the Cognito Hosted-UI redirect with an in-app flow against pollis-auth, so the
 * user never leaves landpilot.co:
 *   1. phone  -> /auth/pin/send
 *   2. code   -> /auth/login as { phone, pin }   (no separate verify endpoint)
 *
 * The other way in — a ?token=&uid= magic link — is consumed by AuthContext on mount and
 * never reaches this screen.
 */

const NAVY = "#0d1b2a";
const MUTED = "#a0b4c8";
const RED = "#ff0a4b"; // errors only
const ACCENT = "#074d63"; // primary action
// pollis-auth issues a 4-digit code. (The Pollis client's maxLength={6} is looser than
// what the API actually sends — don't copy it, it makes the button unreachable.)
const PIN_LENGTH = 4;

/** Raw digits in state; this only formats for display. */
const maskPhone = (d) => {
  const s = String(d).replace(/\D/g, "").slice(0, 10);
  if (s.length <= 3) return s;
  if (s.length <= 6) return `(${s.slice(0, 3)}) ${s.slice(3)}`;
  return `(${s.slice(0, 3)}) ${s.slice(3, 6)}-${s.slice(6)}`;
};
const toE164 = (d) => {
  const s = String(d).replace(/\D/g, "");
  return "+" + (s.length === 10 ? "1" + s : s);
};

const inputStyle = {
  width: "100%",
  boxSizing: "border-box",
  padding: "11px 13px",
  fontSize: 15,
  fontFamily: "'Inter', sans-serif",
  color: "#fff",
  background: "rgba(255,255,255,0.06)",
  border: "1px solid #1a3d5c",
  borderRadius: 6,
  outline: "none",
  letterSpacing: "0.02em",
};

const labelStyle = {
  display: "block",
  fontSize: 10,
  fontWeight: 800,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: MUTED,
  marginBottom: 6,
};

const primaryBtn = (disabled) => ({
  background: ACCENT,
  color: "#fff",
  border: "none",
  borderRadius: 6,
  padding: "11px 18px",
  fontSize: 13,
  fontWeight: 700,
  letterSpacing: "0.03em",
  cursor: disabled ? "default" : "pointer",
  opacity: disabled ? 0.55 : 1,
  fontFamily: "'Inter', sans-serif",
  width: "100%",
});

const linkBtn = {
  background: "none",
  border: "none",
  color: MUTED,
  fontSize: 12,
  cursor: "pointer",
  fontFamily: "'Inter', sans-serif",
  textDecoration: "underline",
};

export default function AuthGate({ children }) {
  const { isLoggedIn, loading, login } = useAuth();
  const [step, setStep] = useState("phone"); // phone | pin
  const [phone, setPhone] = useState("");
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const pinRef = useRef(null);

  // This component never unmounts — it swaps between the form and `children` — so form
  // state outlives a whole session. Without this reset, signing out drops you back onto
  // step 2 with the spent code still in the box and the button stuck on "Verifying…"
  // (`busy` is deliberately left true on success, since nothing re-renders the form).
  const wasLoggedIn = useRef(false);
  useEffect(() => {
    if (isLoggedIn) {
      wasLoggedIn.current = true;
    } else if (wasLoggedIn.current) {
      wasLoggedIn.current = false;
      setStep("phone");
      setPhone("");
      setPin("");
      setErr("");
      setBusy(false);
    }
  }, [isLoggedIn]);

  if (loading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: NAVY,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <l-helix size="50" speed="2.5" color="#ffffff"></l-helix>
      </div>
    );
  }

  if (isLoggedIn) return children;

  const digits = phone.replace(/\D/g, "");
  const phoneValid = digits.length >= 10;

  const send = async (e) => {
    e?.preventDefault();
    if (!phoneValid || busy) return;
    setErr("");
    setBusy(true);
    try {
      await sendPin({ phone: toE164(digits) });
      setStep("pin");
      setPin("");
      setTimeout(() => pinRef.current?.focus(), 50);
    } catch {
      setErr("Couldn't text a code to that number. Check it and try again.");
    }
    setBusy(false);
  };

  const verify = async (code = pin) => {
    if (code.length < PIN_LENGTH || busy) return;
    setErr("");
    setBusy(true);
    try {
      await login({ phone: toE164(digits), pin: code });
      // Success swaps this screen for the app. `busy` stays true so the form can't be
      // re-submitted during the swap; the sign-out effect above clears it.
    } catch {
      setErr("That code didn't work — try again.");
      setPin("");
      setBusy(false);
      setTimeout(() => pinRef.current?.focus(), 50);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: NAVY,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "'Inter', sans-serif",
        padding: 24,
      }}
    >
      <img
        src="/landpilot_logo_light.svg"
        alt="LandPilot"
        style={{ height: 125, opacity: 0.9 }}
      />
      {/* Spacing lives here rather than a container gap, so it reads as a flat 40px */}
      <div
        style={{
          width: "100%",
          maxWidth: 320,
          display: "grid",
          gap: 14,
          marginTop: 40,
        }}
      >
        {step === "phone" ? (
          <form onSubmit={send} style={{ display: "grid", gap: 14 }}>
            <div>
              <label style={labelStyle} htmlFor="lp-phone">
                Mobile number
              </label>
              <input
                id="lp-phone"
                type="tel"
                inputMode="numeric"
                autoComplete="tel"
                placeholder="(843) 555-0199"
                value={maskPhone(phone)}
                onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
                style={inputStyle}
                autoFocus
              />
            </div>
            <button type="submit" disabled={!phoneValid || busy} style={primaryBtn(!phoneValid || busy)}>
              {busy ? "Sending…" : "Text me a code"}
            </button>
            <div style={{ color: MUTED, fontSize: 11, textAlign: "center", lineHeight: 1.6 }}>
              We'll send a one-time code to verify it's you.
            </div>
          </form>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              verify();
            }}
            style={{ display: "grid", gap: 14 }}
          >
            <div>
              <label style={labelStyle} htmlFor="lp-pin">
                Code sent to {maskPhone(phone)}
              </label>
              <input
                id="lp-pin"
                ref={pinRef}
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={PIN_LENGTH}
                value={pin}
                onChange={(e) =>
                  setPin(e.target.value.replace(/\D/g, "").slice(0, PIN_LENGTH))
                }
                style={{
                  ...inputStyle,
                  textAlign: "center",
                  fontSize: 26,
                  fontWeight: 700,
                  letterSpacing: "0.5em",
                  textIndent: "0.5em",
                }}
                autoFocus
              />
            </div>
            <button type="submit" disabled={pin.length < PIN_LENGTH || busy} style={primaryBtn(pin.length < PIN_LENGTH || busy)}>
              {busy ? "Verifying…" : "Verify & continue"}
            </button>
            <div style={{ display: "flex", justifyContent: "center", gap: 16 }}>
              <button type="button" style={linkBtn} onClick={send} disabled={busy}>
                Resend code
              </button>
              <button
                type="button"
                style={linkBtn}
                onClick={() => {
                  setStep("phone");
                  setPin("");
                  setErr("");
                }}
              >
                Use a different number
              </button>
            </div>
          </form>
        )}

        {err && (
          <div style={{ color: RED, fontSize: 12, textAlign: "center", lineHeight: 1.5 }}>
            {err}
          </div>
        )}
      </div>
    </div>
  );
}
